/**
 * Batterisimulering med fyra strategier.
 *
 * Modellen är timupplöst (eller kvartersupplöst om man matar in
 * 15-minutersvärden, koden gör inga antaganden om steglängden, den
 * räknar bara på de tidssteg den får).
 *
 * Batterimodellen är AC-kopplad och förenklad:
 *   - SoC mäts i kWh
 *   - Laddning och urladdning är begränsade av maxPowerKW per timme
 *   - Round trip-förluster fördelas symmetriskt: chargeEff = dischargeEff
 *     = sqrt(roundTripEfficiency)
 *   - Vi modellerar inte degradering eller temperaturberoende verkningsgrad
 *
 * Returvärdet innehåller hela timspåret så frontend kan rita upp
 * tidsserier, samt aggregerade nyckeltal annualiserade till ett år.
 */

import type {
  BatteryConfig,
  HourPoint,
  ResultHour,
  Scenario,
  SimulationResult,
  Strategy,
} from "./types";
import {
  hourlyEnergyCost,
  calculateMonthlyFees,
  importPricePerKWh,
  exportRevenuePerKWh,
} from "./costs";
import { optimizeWithDP, optimizeWithDPPeakAware } from "./dp-optimizer";
import { calculateAncillaryRevenue, calculatePowerTariff } from "./extra-costs";
import { solveOptimalPolicy, type DPPolicy } from "./optimal-dp";
import { precomputeRolling24hPolicy } from "./rolling24h";
import { calculateIntraHourArbitrage } from "./intra-hour-arbitrage";

interface StepDecision {
  /** AC-sida: kWh som flödar IN i batteriet under timmen (efter laddförluster blir det chargeEff * detta i SoC). */
  chargeFromSolar: number;
  chargeFromGrid: number;
  /** AC-sida: kWh som flödar UT från batteriet under timmen. */
  discharge: number;
}

interface BatteryState {
  socKWh: number;
  cumulativeDischargedKWh: number;
}

const ZERO_DECISION: StepDecision = {
  chargeFromSolar: 0,
  chargeFromGrid: 0,
  discharge: 0,
};

// ----------------------------------------------------------------------------
// Strategi 1: bara sol
// ----------------------------------------------------------------------------

function solarOnlyDecision(
  hour: HourPoint,
  state: BatteryState,
  battery: BatteryConfig,
  chargeEff: number,
  dischargeEff: number,
): StepDecision {
  const minSoc = battery.capacityKWh * battery.minSocFraction;
  const headroomKWh = battery.capacityKWh - state.socKWh;
  const usableKWh = state.socKWh - minSoc;

  if (hour.exportKWh > 0) {
    // Solöverskott finns: ladda upp till min(export, maxPower, headroom/chargeEff).
    const chargeAC = Math.min(
      hour.exportKWh,
      battery.maxPowerKW,
      headroomKWh / chargeEff,
    );
    return { chargeFromSolar: chargeAC, chargeFromGrid: 0, discharge: 0 };
  }

  if (hour.importKWh > 0 && usableKWh > 0) {
    // Vi importerar: urladda för att täcka, upp till min(import, maxPower, usable*dischargeEff).
    const dischargeAC = Math.min(
      hour.importKWh,
      battery.maxPowerKW,
      usableKWh * dischargeEff,
    );
    return { chargeFromSolar: 0, chargeFromGrid: 0, discharge: dischargeAC };
  }

  return ZERO_DECISION;
}

// ----------------------------------------------------------------------------
// Strategi 2: sol + nattladdning på billiga timmar
// ----------------------------------------------------------------------------

interface PriceThresholds {
  cheapThreshold: number;
  expensiveThreshold: number;
  arbitrageWorthwhile: boolean;
}

/**
 * Räknar ut prispercentiler för hela dataserien som tröskelvärden.
 *
 * Använder 10:e och 85:e percentilen för att vara konservativ:
 * arbitrage händer bara på riktigt billiga och riktigt dyra timmar.
 *
 * Sätter också arbitrageWorthwhile till true bara om spreaden mellan
 * percentilerna täcker rundtursförlusterna. Annars är arbitrage
 * principiellt olönsamt och strategin faller tillbaka till bara sol.
 */
function computePriceThresholds(
  hours: HourPoint[],
  roundTripEff: number,
): PriceThresholds {
  const sorted = hours.map((h) => h.spotPrice).sort((a, b) => a - b);
  const pick = (p: number) => sorted[Math.floor(sorted.length * p)] ?? 0;
  const cheap = pick(0.1);
  const expensive = pick(0.85);

  // Break-even för arbitrage på SPOT-priset (vi struntar i fasta öre/kWh-
  // komponenter eftersom de är samma vid både ladd- och urladd-tillfället
  // och slår ut varandra ekonomiskt på MARGINELLT import-byte i tid).
  // Riktig villkor: expensive_price > cheap_price / roundTripEff
  const arbitrageWorthwhile = expensive > cheap / roundTripEff;

  return {
    cheapThreshold: cheap,
    expensiveThreshold: expensive,
    arbitrageWorthwhile,
  };
}

function solarNightChargeDecision(
  hour: HourPoint,
  state: BatteryState,
  battery: BatteryConfig,
  thresholds: PriceThresholds,
  chargeEff: number,
  dischargeEff: number,
): StepDecision {
  // Sol-egenanvändning har alltid företräde och är alltid lönsamt.
  const solarDecision = solarOnlyDecision(
    hour,
    state,
    battery,
    chargeEff,
    dischargeEff,
  );
  if (
    solarDecision.chargeFromSolar > 0 ||
    solarDecision.discharge > 0
  ) {
    return solarDecision;
  }

  // VIKTIGT: solar-night-charge MÅSTE vara en strikt superset av solar-only.
  // Tidigare bug: urladdade BARA vid dyra timmar (topp 15%) vilket gjorde
  // att lagrad solenergi aldrig urladdades på medium-prisdagar. Resultat:
  // sämre besparing än solar-only.
  //
  // Nu: urladda greedy mot import precis som solar-only gör. Nattladdning
  // läggs PÅ TOPPEN, inte istället för.
  const minSoc = battery.capacityKWh * battery.minSocFraction;
  const headroomKWh = battery.capacityKWh - state.socKWh;
  const usableKWh = state.socKWh - minSoc;

  // Urladda greedy om vi har energi och det finns import.
  if (hour.importKWh > 0 && usableKWh > 0) {
    const dischargeAC = Math.min(
      hour.importKWh,
      battery.maxPowerKW,
      usableKWh * dischargeEff,
    );
    return { chargeFromSolar: 0, chargeFromGrid: 0, discharge: dischargeAC };
  }

  // Nattladdning från nätet bara om:
  //   - priset är i de billigaste 10% av fönstret
  //   - arbitrage är principiellt lönsamt
  //   - SoC är låg (under 30% — lämnar rum för morgondagens sol)
  //   - vi inte exporterar samtidigt
  if (!thresholds.arbitrageWorthwhile) return ZERO_DECISION;

  const isCheap = hour.spotPrice <= thresholds.cheapThreshold;
  if (
    isCheap &&
    state.socKWh < battery.capacityKWh * 0.3 &&
    hour.exportKWh === 0 &&
    headroomKWh > 0.5
  ) {
    const targetSoc = battery.capacityKWh * 0.5;
    const wanted = Math.max(0, targetSoc - state.socKWh);
    const chargeAC = Math.min(wanted / chargeEff, battery.maxPowerKW);
    if (chargeAC > 0) {
      return { chargeFromSolar: 0, chargeFromGrid: chargeAC, discharge: 0 };
    }
  }

  return ZERO_DECISION;
}

// ----------------------------------------------------------------------------
// Strategi 3: rullande 24h-optimering (DP över diskretiserad SoC)
// ----------------------------------------------------------------------------

/**
 * 24h-prediktion med ranking-baserad ansats.
 *
 * Algoritm:
 *   1. Sol-överskott till batteri (alltid lönsamt).
 *   2. Identifiera N "billigaste" och N "dyraste" timmar i fönstret där
 *      N = timmar som behövs för att fylla batteriet vid maxeffekt.
 *   3. Om aktuell timme är bland de billigaste OCH arbitrage är lönsamt:
 *      ladda från nätet.
 *   4. Om aktuell timme är bland de dyraste (eller över medel) och vi har
 *      stored energy: urladda mot import.
 *   5. Om aktuell timme är "billig" men inte topp och vi har import:
 *      vänta (håll energin för dyrare timmar).
 *
 * Reservera utrymme för förväntad kommande sol så grid-laddning inte
 * stjäl plats från solöverskott.
 */
function predictive24hDecision(
  windowHours: HourPoint[],
  state: BatteryState,
  battery: BatteryConfig,
  chargeEff: number,
  dischargeEff: number,
  effectiveImportCost: (h: HourPoint) => number,
): StepDecision {
  if (windowHours.length === 0) return ZERO_DECISION;

  const current = windowHours[0];
  const minSoc = battery.capacityKWh * battery.minSocFraction;
  const headroomKWh = battery.capacityKWh - state.socKWh;
  const usableKWh = state.socKWh - minSoc;
  const roundTrip = chargeEff * dischargeEff;

  // Regel 1: solöverskott till batteri
  if (current.exportKWh > 0 && headroomKWh > 0.05) {
    const chargeAC = Math.min(
      current.exportKWh,
      headroomKWh / chargeEff,
      battery.maxPowerKW,
    );
    return { chargeFromSolar: chargeAC, chargeFromGrid: 0, discharge: 0 };
  }

  // Räkna effektiva priser för fönstret
  const costs = windowHours.map((h) => effectiveImportCost(h));
  const currentCost = costs[0] ?? 0;

  // Reservera utrymme för förväntad sol
  const upcomingSolarOverflow = windowHours
    .slice(1)
    .reduce((s, h) => s + h.exportKWh, 0);
  const reservedForSolar = Math.min(battery.capacityKWh, upcomingSolarOverflow);
  const availableHeadroom = Math.max(0, headroomKWh - reservedForSolar);

  // Hitta de N billigaste och N dyraste timmarna baserat på batteriets
  // laddningskapacitet. Plus en marginalbuffert för att utnyttja kapacitet.
  const hoursToFill = Math.max(
    1,
    Math.ceil(battery.capacityKWh / (battery.maxPowerKW * chargeEff)),
  );
  const dischargeHoursBudget = hoursToFill + 2;

  const indexedCosts = costs.map((c, i) => ({ i, c }));
  const sortedAsc = [...indexedCosts].sort((a, b) => a.c - b.c);
  const cheapestSet = new Set(
    sortedAsc.slice(0, hoursToFill).map((h) => h.i),
  );
  const expensiveSet = new Set(
    sortedAsc.slice(-dischargeHoursBudget).map((h) => h.i),
  );

  const avgCheapCost =
    sortedAsc.slice(0, hoursToFill).reduce((s, h) => s + h.c, 0) /
    hoursToFill;
  const avgExpensiveCost =
    sortedAsc.slice(-dischargeHoursBudget).reduce((s, h) => s + h.c, 0) /
    dischargeHoursBudget;

  // Är arbitrage värt att göra? Lönsam om dyraste hour * roundTrip
  // överstiger billigaste hour med tillräcklig marginal.
  const arbitrageMargin = avgExpensiveCost * roundTrip - avgCheapCost;
  const arbitrageProfitable = arbitrageMargin > 0.05; // > 5 öre vinst per kWh

  // Regel 2: ladda från nätet om vi är i billiga setet och arbitrage finns
  if (
    current.exportKWh === 0 &&
    cheapestSet.has(0) &&
    availableHeadroom > 0.5 &&
    arbitrageProfitable
  ) {
    const chargeAC = Math.min(
      availableHeadroom / chargeEff,
      battery.maxPowerKW,
    );
    return { chargeFromSolar: 0, chargeFromGrid: chargeAC, discharge: 0 };
  }

  // Regel 3: urladda mot import. Greedy så länge vi inte är på en av
  // fönstrets allra billigaste timmar (där det är bättre att vänta eller
  // ladda än att tömma batteriet).
  if (
    current.importKWh > 0 &&
    usableKWh > 0 &&
    !cheapestSet.has(0)
  ) {
    const dischargeAC = Math.min(
      current.importKWh,
      battery.maxPowerKW,
      usableKWh * dischargeEff,
    );
    return { chargeFromSolar: 0, chargeFromGrid: 0, discharge: dischargeAC };
  }

  return ZERO_DECISION;
}

// ----------------------------------------------------------------------------
// Strategi 4: perfekt framförhållning (kommer i nästa iteration)
// ----------------------------------------------------------------------------
//
// Detta är ett LP-problem:
//   minimera   sum_t (importCost_t * netImport_t - exportRevenue_t * netExport_t)
//   över      charge_t, discharge_t, soc_t
//   under    soc_t = soc_{t-1} + chargeEff*charge_t - discharge_t/dischargeEff
//            minSoc <= soc_t <= capacity
//            0 <= charge_t <= maxPower
//            0 <= discharge_t <= maxPower
//            netImport_t = max(0, load_t - solar_t - solarToBattery_t + discharge_t)
//
// LP-lösning i webbläsaren via glpk.js är möjlig men tar ~5-30 sek på
// 8760 timmar. Vi lägger till det i V2. För nu använder vi
// predictive24h som proxy och flaggar "optimal" som "ej implementerad
// ännu" så vi inte överlovar.

// ----------------------------------------------------------------------------
// Simuleringskärnan
// ----------------------------------------------------------------------------

function applyBatteryStep(
  decision: StepDecision,
  state: BatteryState,
  battery: BatteryConfig,
  chargeEff: number,
  dischargeEff: number,
): BatteryState {
  // Energi som tillkommer i batteriet (DC-sidan): chargeAC * chargeEff
  const energyIn =
    (decision.chargeFromSolar + decision.chargeFromGrid) * chargeEff;
  // Energi som lämnar batteriet (DC-sidan): dischargeAC / dischargeEff
  const energyOut = decision.discharge / dischargeEff;
  return {
    socKWh: state.socKWh + energyIn - energyOut,
    cumulativeDischargedKWh:
      state.cumulativeDischargedKWh + decision.discharge,
  };
}

function netHourEnergy(
  hour: HourPoint,
  decision: StepDecision,
): { importKWh: number; exportKWh: number } {
  // Logik:
  //   baselineImport = hour.importKWh, baselineExport = hour.exportKWh
  //   urladdning minskar import krona för krona
  //   nätladdning ökar import krona för krona
  //   sol-laddning minskar export krona för krona
  const importKWh = Math.max(
    0,
    hour.importKWh - decision.discharge + decision.chargeFromGrid,
  );
  const exportKWh = Math.max(0, hour.exportKWh - decision.chargeFromSolar);
  return { importKWh, exportKWh };
}

function decideStep(
  strategy: Strategy,
  index: number,
  hours: HourPoint[],
  state: BatteryState,
  battery: BatteryConfig,
  thresholds: PriceThresholds,
  chargeEff: number,
  dischargeEff: number,
  effectiveImportCost: (h: HourPoint) => number,
  dpPolicy?: DPPolicy,
): StepDecision {
  const current = hours[index];
  switch (strategy) {
    case "solar-only":
      return solarOnlyDecision(current, state, battery, chargeEff, dischargeEff);

    case "solar-night-charge":
      return solarNightChargeDecision(
        current,
        state,
        battery,
        thresholds,
        chargeEff,
        dischargeEff,
      );

    case "predictive-24h": {
      // Om rolling 24h-DP-policy är förberäknad: använd den. Det är vad
      // ett verkligt batterisystem gör (DP-optimering inom Nordpool-
      // fönstret). Översättning till StepDecision är samma som
      // optimal-foresight.
      if (dpPolicy && index < dpPolicy.acFlow.length) {
        const acFlow = dpPolicy.acFlow[index] ?? 0;
        if (acFlow > 0) {
          const chargeFromSolar = Math.min(acFlow, current!.exportKWh);
          const chargeFromGrid = acFlow - chargeFromSolar;
          return {
            chargeFromSolar,
            chargeFromGrid,
            discharge: 0,
          };
        } else if (acFlow < 0) {
          return {
            chargeFromSolar: 0,
            chargeFromGrid: 0,
            discharge: -acFlow,
          };
        }
        return ZERO_DECISION;
      }
      // Fallback: heuristik (om policy inte kunde precomputas av någon
      // anledning, t.ex. tom timserie).
      const window = hours.slice(index, index + 24);
      return predictive24hDecision(
        window,
        state,
        battery,
        chargeEff,
        dischargeEff,
        effectiveImportCost,
      );
    }

    case "optimal-foresight": {
      // DP-policy är förberäknad. Översätt acFlow till StepDecision.
      if (!dpPolicy || index >= dpPolicy.acFlow.length) return ZERO_DECISION;
      const acFlow = dpPolicy.acFlow[index] ?? 0;
      if (acFlow > 0) {
        // Laddar. Ta solöverskott först, resten från nätet.
        const chargeFromSolar = Math.min(acFlow, current.exportKWh);
        const chargeFromGrid = acFlow - chargeFromSolar;
        return {
          chargeFromSolar,
          chargeFromGrid,
          discharge: 0,
        };
      } else if (acFlow < 0) {
        return {
          chargeFromSolar: 0,
          chargeFromGrid: 0,
          discharge: -acFlow,
        };
      }
      return ZERO_DECISION;
    }
  }
}

/**
 * Kör simulering över hela timserien och returnera resultatobjekt.
 */
/**
 * Intern simulering — kallas från externa simulate() med eller utan
 * importCap. Cap används som safety net för heuristik-strategier när
 * effekttariff är aktiv: utan medvetenhet om månadens topp-N kan
 * heuristiker ladda batteriet på timmar som skapar nya peaks och
 * därmed försämra totalbesparing.
 */
function simulateInternal(
  hours: HourPoint[],
  scenario: Scenario,
  importCap?: number[],
): SimulationResult {
  if (hours.length === 0) {
    throw new Error("Kan inte simulera utan timdata.");
  }

  const { battery, strategy, tax, supplier, grid, ancillary } = scenario;

  // "optimal-foresight" använder DP-optimering istället för heuristik
  // för att hitta det matematiskt bästa beslutet givet hela perioden
  // som känd. Det är guld-standarden för jämförelse.
  if (strategy === "optimal-foresight") {
    return runDPSimulation(hours, scenario);
  }

  const chargeEff = Math.sqrt(battery.roundTripEfficiency);
  const dischargeEff = Math.sqrt(battery.roundTripEfficiency);

  const thresholds = computePriceThresholds(hours, battery.roundTripEfficiency);

  // Approximerat effektivt importpris (kr/kWh inkl moms) för predictive
  // strategin. Vi gissar tier-nivån (skip tier-detaljer för predictive --
  // vi använder den enkla transmissionen som approximation eftersom vi
  // inte vet batteri-scenariots totalimport förrän efter simuleringen).
  // Det räcker för att jämföra timmar mot varandra inom samma fönster.
  const effectiveImportCost = (h: HourPoint): number => {
    const exVat =
      h.spotPrice +
      supplier.importMarkupPerKWh +
      tax.energyTaxPerKWh +
      grid.transmissionPerKWh;
    return exVat * (1 + tax.vatRate);
  };

  let state: BatteryState = {
    socKWh: battery.capacityKWh * battery.minSocFraction,
    cumulativeDischargedKWh: 0,
  };

  // Förbered rolling 24h-DP-policy för predictive-24h-strategin. Detta
  // är vad ett verkligt batterisystem gör: DP-optimering över det
  // Nordpool-fönster som är publicerat just nu (typiskt 11-34h fram-
  // förhållning), uppdaterat vid varje 13:00.
  let rollingPolicy: DPPolicy | undefined;
  if (strategy === "predictive-24h" && hours.length > 0) {
    // Använd samma effektiva import/export-priser som heuristiken (utan
    // tier-detaljer eftersom vi inte vet totalimport förrän efteråt).
    const exportRevenueApprox = (h: HourPoint): number => {
      return (
        h.spotPrice -
        supplier.exportMarkdownPerKWh +
        supplier.exportBonusPerKWh +
        grid.feedInCompensationPerKWh
      );
    };
    rollingPolicy = precomputeRolling24hPolicy(
      hours,
      battery,
      effectiveImportCost,
      exportRevenueApprox,
      { socLevels: 21, startSocFraction: battery.minSocFraction },
    );
  }

  const result: ResultHour[] = [];

  // Pass 1: bestäm batterihandlingar och beräkna nya import/export-flöden.
  // Vi behöver ackumulera totalerna för båda scenarierna FÖRST, eftersom
  // tröskelmodellen (E.on m.fl.) väljer tariff baserat på årsförbrukning.
  interface PassOneEntry {
    hour: HourPoint;
    decision: StepDecision;
    importKWh: number;
    exportKWh: number;
    socKWh: number;
  }
  const passOne: PassOneEntry[] = [];
  let batteryAnnualImport = 0;

  for (let i = 0; i < hours.length; i++) {
    const hour = hours[i];
    const decision = decideStep(
      strategy,
      i,
      hours,
      state,
      battery,
      thresholds,
      chargeEff,
      dischargeEff,
      effectiveImportCost,
      rollingPolicy,
    );

    // Cap-medvetenhet: om effekttariff är aktiv kan heuristiker ladda
    // batteriet på timmar som drar upp grid-importen över månadens
    // topp-N och skapa nya peaks. Vi justerar ner chargeFromGrid så
    // imports stannar inom cap. Heuristiken får mindre arbitrage-besparing
    // men inga nya peaks. Det yttre safety net väljer den bästa varianten.
    if (importCap) {
      const { importKWh: projectedImport } = netHourEnergy(hour, decision);
      if (projectedImport > importCap[i]!) {
        const excess = projectedImport - importCap[i]!;
        decision.chargeFromGrid = Math.max(0, decision.chargeFromGrid - excess);
      }
    }

    const { importKWh, exportKWh } = netHourEnergy(hour, decision);
    state = applyBatteryStep(
      decision,
      state,
      battery,
      chargeEff,
      dischargeEff,
    );
    batteryAnnualImport += importKWh;
    passOne.push({
      hour,
      decision,
      importKWh,
      exportKWh,
      socKWh: state.socKWh,
    });
  }

  // Annualisera importtotalerna så tröskelmodellen får rätt input
  // även när data inte är exakt ett år.
  const periodHours = hours.length;
  const hourlyFactor = 8760 / periodHours;
  const baselineAnnualImportRaw = hours.reduce((s, h) => s + h.importKWh, 0);
  const baselineAnnualImportAnnualized = baselineAnnualImportRaw * hourlyFactor;
  const batteryAnnualImportAnnualized = batteryAnnualImport * hourlyFactor;

  // Pass 2: räkna timkostnader med rätt tier-kontext för varje scenario.
  let baselineTotalSek = 0;
  let batteryTotalSek = 0;

  for (const entry of passOne) {
    const hourCost = hourlyEnergyCost(
      entry.hour,
      entry.importKWh,
      entry.exportKWh,
      tax,
      supplier,
      grid,
      batteryAnnualImportAnnualized,
    );
    const baselineCost = hourlyEnergyCost(
      entry.hour,
      entry.hour.importKWh,
      entry.hour.exportKWh,
      tax,
      supplier,
      grid,
      baselineAnnualImportAnnualized,
    );
    baselineTotalSek += baselineCost;
    batteryTotalSek += hourCost;
    result.push({
      from: entry.hour.from,
      importKWh: entry.importKWh,
      exportKWh: entry.exportKWh,
      batteryChargeFromSolarKWh: entry.decision.chargeFromSolar,
      batteryChargeFromGridKWh: entry.decision.chargeFromGrid,
      batteryDischargeKWh: entry.decision.discharge,
      socKWh: entry.socKWh,
      netCostSek: hourCost,
    });
  }

  // Effekttariff och fast avgift räknas månadsvis och bygger på den
  // FAKTISKA importprofilen efter batteriet. Baseline använder ursprunglig.
  const monthlyBattery = calculateMonthlyFees(result, grid, tax);
  const baselineResultLike: ResultHour[] = hours.map((h) => ({
    from: h.from,
    importKWh: h.importKWh,
    exportKWh: h.exportKWh,
    batteryChargeFromSolarKWh: 0,
    batteryChargeFromGridKWh: 0,
    batteryDischargeKWh: 0,
    socKWh: 0,
    netCostSek: 0,
  }));
  const monthlyBaseline = calculateMonthlyFees(baselineResultLike, grid, tax);

  // Annualisering: tim- och månadsdelen skalas olika.
  // Timkostnader skalas baserat på antal timmar (8760 / hours.length).
  // Månadsavgifter skalas baserat på antal månader i datan (12 / months).
  const monthsCovered = new Set(hours.map((h) => h.from.slice(0, 7))).size;
  const monthlyFactor = 12 / Math.max(monthsCovered, 1);

  baselineTotalSek =
    baselineTotalSek * hourlyFactor +
    monthlyBaseline.totalSek * monthlyFactor;
  batteryTotalSek =
    batteryTotalSek * hourlyFactor + monthlyBattery.totalSek * monthlyFactor;

  const annualizationFactor = hourlyFactor; // för cykler etc nedan

  const fullCycles =
    state.cumulativeDischargedKWh /
    Math.max(battery.capacityKWh * (1 - battery.minSocFraction), 1);

  const baselineExportTotal = hours.reduce((s, h) => s + h.exportKWh, 0);
  const batteryExportTotal = result.reduce((s, h) => s + h.exportKWh, 0);
  const selfConsumptionUplift =
    baselineExportTotal > 0
      ? (baselineExportTotal - batteryExportTotal) / baselineExportTotal
      : 0;

  const ancillaryRevenue = ancillary.enabled
    ? calculateAncillaryRevenue(ancillary, battery).netAnnualAverage
    : 0;

  const annualBaseline = baselineTotalSek;
  const annualBattery = batteryTotalSek - ancillaryRevenue;
  const annualSavings = annualBaseline - annualBattery;

  const paybackYears =
    battery.investmentSek && battery.investmentSek > 0 && annualSavings > 0
      ? battery.investmentSek / annualSavings
      : undefined;

  return {
    scenario,
    hours: result,
    periodFrom: hours[0].from,
    periodTo: hours[hours.length - 1].from,
    annual: {
      baselineCostSek: annualBaseline,
      batteryCostSek: annualBattery,
      savingsSek: annualSavings,
      fullCycles: fullCycles * annualizationFactor,
      selfConsumptionUplift,
      ancillaryRevenueSek: ancillaryRevenue,
    },
    paybackYears,
  };
}

export {
  importPricePerKWh,
  exportRevenuePerKWh,
  hourlyEnergyCost,
  calculateMonthlyFees,
};

/**
 * Beräkna per-timme importCap baserat på baseline månads-topp-N.
 * Används som safety net så heuristiker inte skapar nya peaks när
 * effekttariff är aktiv.
 */
function computeMonthlyImportCap(
  hours: HourPoint[],
  topHoursAveraged: number,
): number[] {
  const monthIndexArr = hours.map((h) => h.from.slice(0, 7));
  const monthSet = new Set(monthIndexArr);
  const monthCap = new Map<string, number>();
  for (const m of monthSet) {
    const monthImports: number[] = [];
    for (let t = 0; t < hours.length; t++) {
      if (monthIndexArr[t] === m) monthImports.push(hours[t]!.importKWh);
    }
    monthImports.sort((a, b) => b - a);
    monthCap.set(
      m,
      monthImports[Math.min(topHoursAveraged - 1, monthImports.length - 1)] ?? 0,
    );
  }
  return hours.map((_, t) => monthCap.get(monthIndexArr[t]!)!);
}

/**
 * Extern simulate-funktion. Wrappar simulateInternal med safety net:
 * när effekttariff är aktiv kör vi heuristikerna både fritt och med
 * importCap, och väljer den med högst total besparing. Garanterar att
 * aktivering av effekttariff aldrig minskar besparingen för heuristiker.
 *
 * DP-strategin (optimal-foresight) har sin egen safety net inuti
 * runDPSimulation (tre-vägs).
 */
export function simulate(
  hours: HourPoint[],
  scenario: Scenario,
): SimulationResult {
  const { strategy, grid } = scenario;

  // DP har egen safety net inbyggd
  if (strategy === "optimal-foresight") {
    return simulateInternal(hours, scenario);
  }

  const hasPowerTariff =
    grid.powerTariff &&
    grid.powerTariff.pricePerKWPerMonth > 0 &&
    (grid.powerTariff.activateFromYear ?? 0) === 0;

  // Utan effekttariff behövs ingen safety net
  if (!hasPowerTariff || !grid.powerTariff) {
    return simulateInternal(hours, scenario);
  }

  // Med effekttariff: kör heuristiken med tre varianter och välj den
  // med högst total besparing. Garanti: aktivering av effekttariff
  // aldrig minskar besparing.
  //
  //   1. Fri: kan skapa nya peaks via grid-laddning (max arbitrage)
  //   2. Top-N cap: chargeFromGrid begränsas av månadens topp-N
  //   3. Per-timme cap = baseline import: förbjuder all grid-laddning,
  //      garanterar att inga nya peaks skapas (urladdning hjälper kapa)
  //
  // Variant 3 är säkerhetsnivån — den är fysiskt ekvivalent med
  // solar-only-beteende men behåller urladdningsmönstret från
  // strategin (urladdar fortfarande på dyra timmar = kan sänka peaks).
  const topNCap = computeMonthlyImportCap(
    hours,
    grid.powerTariff.topHoursAveraged,
  );
  const perHourCap = hours.map((h) => h.importKWh);

  const freeResult = simulateInternal(hours, scenario);
  const topNCapResult = simulateInternal(hours, scenario, topNCap);
  const zeroChargeResult = simulateInternal(hours, scenario, perHourCap);

  const candidates = [freeResult, topNCapResult, zeroChargeResult];
  return candidates.reduce((best, r) =>
    r.annual.savingsSek > best.annual.savingsSek ? r : best,
  );
}

/**
 * Kör DP-optimerare och konvertera till SimulationResult-format.
 * Detta är den "Optimal (LP)"-strategin: garanterat nära-optimal lösning
 * via dynamisk programmering. Diskretiserad i SoC-nivåer.
 */
function runDPSimulation(
  hours: HourPoint[],
  scenario: Scenario,
): SimulationResult {
  const { battery, tax, supplier, grid, ancillary } = scenario;

  // Om stödtjänster är på reserveras en del av batteriet för FCR-D-aggregatorn.
  // Den delen är inte tillgänglig för arbitrage/egenanvändning. Vi modellerar
  // det som minskad effektiv kapacitet. maxPowerKW lämnas oförändrad eftersom
  // FCR-D kräver full effekttillgänglighet — det är energi-bufferten som krymper.
  const effectiveBattery: BatteryConfig =
    ancillary.enabled && ancillary.reservedCapacityFraction > 0
      ? {
          ...battery,
          capacityKWh:
            battery.capacityKWh * (1 - ancillary.reservedCapacityFraction),
        }
      : battery;

  // 31 SoC-nivåer ger ~5% diskretiseringsfel jämfört med kontinuerlig LP,
  // körs på ~250 ms för 8760 timmar. God balans mellan precision och UX.
  //
  // Om effekttariff är aktiv (powerTariff finns och activateFromYear=0),
  // använd peak-aware DP som via shadow pricing optimerar batteriet att
  // även kapa månadens topp-N timmar. Annars vanlig DP.
  const hasPowerTariff =
    grid.powerTariff &&
    grid.powerTariff.pricePerKWPerMonth > 0 &&
    (grid.powerTariff.activateFromYear ?? 0) === 0;

  let dp = hasPowerTariff
    ? optimizeWithDPPeakAware(hours, effectiveBattery, tax, supplier, grid, 31)
    : optimizeWithDP(hours, effectiveBattery, tax, supplier, grid, 31);

  // Safety net: peak-aware DP kan i vissa konfigurationer (litet batteri,
  // hög reservering för stödtjänster) producera värre totalresultat än
  // utan tariff-medvetenhet. Vi kör tre varianter och väljer den med
  // högst total besparing:
  //   1. Peak-aware (shadow pricing + hard cap)
  //   2. Vanilla med hard cap (undviker att skapa nya peaks via grid-load)
  //   3. Vanilla utan cap (full DP-frihet, samma som utan effekttariff)
  //
  // Användaren ska aldrig straffas för att slå på effekttariff. Om
  // batteriet är för litet för meningsfull peak-shaving vinner variant 3
  // automatiskt och slutsiffran är garanterat minst lika hög som utan.
  if (hasPowerTariff && grid.powerTariff) {
    const pt = grid.powerTariff;
    const N = pt.topHoursAveraged;
    const monthIndexArr = hours.map((h) => h.from.slice(0, 7));
    const monthSet = new Set(monthIndexArr);
    const monthCap = new Map<string, number>();
    for (const m of monthSet) {
      const monthImports: number[] = [];
      for (let t = 0; t < hours.length; t++) {
        if (monthIndexArr[t] === m) monthImports.push(hours[t]!.importKWh);
      }
      monthImports.sort((a, b) => b - a);
      monthCap.set(
        m,
        monthImports[Math.min(N - 1, monthImports.length - 1)] ?? 0,
      );
    }
    const importCap = hours.map((_, t) => monthCap.get(monthIndexArr[t]!)!);

    const vanillaCappedResult = optimizeWithDP(
      hours,
      effectiveBattery,
      tax,
      supplier,
      grid,
      31,
      undefined,
      importCap,
    );
    const vanillaFreeResult = optimizeWithDP(
      hours,
      effectiveBattery,
      tax,
      supplier,
      grid,
      31,
    );

    const baselineHoursLike: ResultHour[] = hours.map((h) => ({
      from: h.from,
      importKWh: h.importKWh,
      exportKWh: h.exportKWh,
      batteryChargeFromSolarKWh: 0,
      batteryChargeFromGridKWh: 0,
      batteryDischargeKWh: 0,
      socKWh: 0,
      netCostSek: 0,
    }));
    const monthsCov = new Set(hours.map((h) => h.from.slice(0, 7))).size;
    const factor = 12 / Math.max(monthsCov, 1);
    const baselineMb = calculateMonthlyFees(baselineHoursLike, grid, tax);

    function totalSavings(r: typeof dp): number {
      const mb = calculateMonthlyFees(r.hours, grid, tax);
      const baselineTotal =
        r.annualCostSek + r.annualSavingsSek + baselineMb.totalSek * factor;
      const batteryTotal = r.annualCostSek + mb.totalSek * factor;
      return baselineTotal - batteryTotal;
    }

    // Välj den med högst total besparing
    const candidates = [
      { result: dp, score: totalSavings(dp) },
      { result: vanillaCappedResult, score: totalSavings(vanillaCappedResult) },
      { result: vanillaFreeResult, score: totalSavings(vanillaFreeResult) },
    ];
    const best = candidates.reduce((a, b) => (b.score > a.score ? b : a));
    dp = best.result;
  }

  // Lägg på månadsavgifter
  const monthlyBattery = calculateMonthlyFees(dp.hours, grid, tax);
  const baselineResultLike: ResultHour[] = hours.map((h) => ({
    from: h.from,
    importKWh: h.importKWh,
    exportKWh: h.exportKWh,
    batteryChargeFromSolarKWh: 0,
    batteryChargeFromGridKWh: 0,
    batteryDischargeKWh: 0,
    socKWh: 0,
    netCostSek: 0,
  }));
  const monthlyBaseline = calculateMonthlyFees(baselineResultLike, grid, tax);

  const monthsCovered = new Set(hours.map((h) => h.from.slice(0, 7))).size;
  const monthlyFactor = 12 / Math.max(monthsCovered, 1);

  const annualBaselineWithFees =
    (dp.annualCostSek + dp.annualSavingsSek) +
    monthlyBaseline.totalSek * monthlyFactor;
  const annualBatteryWithFees =
    dp.annualCostSek + monthlyBattery.totalSek * monthlyFactor;

  const ancillaryRevenue = ancillary.enabled
    ? calculateAncillaryRevenue(ancillary, battery).netAnnualAverage
    : 0;

  const annualBattery = annualBatteryWithFees - ancillaryRevenue;

  // Intra-hour batteri-arbitrage: utnyttja prisvariation mellan kvarter inom
  // samma timme (Nordpool publicerar dagspriser per 15-min sedan okt 2025).
  // Detta är en extra besparing UTÖVER tim-baserad DP eftersom DP arbetar
  // på timupplösning. Volymen begränsas konservativt av husets kvarts-
  // konsumtion för att undvika att modellera export.
  const intraHour = calculateIntraHourArbitrage(hours, battery);
  const annualScaler = monthlyFactor; // skala till helår om perioden < 12 mån
  const intraHourBonusAnnual = intraHour.totalSek * annualScaler;

  const annualSavings =
    annualBaselineWithFees - annualBattery + intraHourBonusAnnual;

  const baselineExportTotal = hours.reduce((s, h) => s + h.exportKWh, 0);
  const batteryExportTotal = dp.hours.reduce((s, h) => s + h.exportKWh, 0);
  const selfConsumptionUplift =
    baselineExportTotal > 0
      ? (baselineExportTotal - batteryExportTotal) / baselineExportTotal
      : 0;

  const paybackYears =
    battery.investmentSek && battery.investmentSek > 0 && annualSavings > 0
      ? battery.investmentSek / annualSavings
      : undefined;

  return {
    scenario,
    hours: dp.hours,
    periodFrom: hours[0]!.from,
    periodTo: hours[hours.length - 1]!.from,
    annual: {
      baselineCostSek: annualBaselineWithFees,
      batteryCostSek: annualBattery,
      savingsSek: annualSavings,
      fullCycles: dp.fullCycles,
      selfConsumptionUplift,
      ancillaryRevenueSek: ancillaryRevenue,
    },
    paybackYears,
  };
}
